import React, { Suspense } from 'react';
import { Navigate, Route, Routes } from 'react-router-dom';
import { APICONSTANTS, CONSTANTS, NAV_ITEMS } from 'src/components/Constants';

// Descendant router mounted by Dashboard at `/patients/*`; route paths are
// relative to `/patients`. `rel()` strips that prefix.
const rel = (p) => p.replace(/^\/patients/, '') || '/';
import BackdropLoader from 'src/components/UI/loader/BackdropLoader';
import {
  UserContext,
  LoadingContext,
  AppContext
} from 'src/store/ContextStore';

import PedigreeChartView from '../pedigree-chart/PedigreeChartView';
import PatientDashboard from './PatientDashboard';
import PatientSettings from './PatientSettings';
import { EVENTS, appReducer } from '../admin/appReducer';
import PatientProband from './PatientProband';
import PatientChat from './patient-chat/PatientChat';
import axios from 'src/axios';

// lazy loading for form builder feature

const ViewFormResponse = React.lazy(() =>
  import('src/components/form-builder/ViewFormResponse')
);

export default function PatientMain({ setNavItems }) {
  const { user } = React.useContext(UserContext);

  // store the ques context
  const [content, dispatchUpdate] = React.useReducer(
    appReducer,
    CONSTANTS.patientInitialContent
  );
  const providerValue = React.useMemo(
    () => ({ content, dispatchUpdate }),
    [content, dispatchUpdate]
  );

  const [loading, setLoading] = React.useState(false);
  const loadingProviderValue = React.useMemo(() => ({ setLoading }), []);

  const {
    settings: { chatFeature },
    selectedGroup
  } = content;

  React.useEffect(() => {
    if (user?.userGroup && user.userGroup.length === 1) {
      dispatchUpdate({
        type: EVENTS.SET_SELECTED_GROUP,
        value: user.userGroup[0]._id
      });
    }
  }, [user]);

  React.useMemo(() => {
    if (selectedGroup && selectedGroup !== 'default') {
      axios
        .get(APICONSTANTS.getChatFeature.replace(':userGroup', selectedGroup))
        .then((res) => {
          dispatchUpdate({
            type: EVENTS.SET_SETTINGS,
            tab: res.data.featureName,
            value: res.data.status
          });
          if (res.data.status) {
            setNavItems(NAV_ITEMS.patient);
          } else {
            setNavItems((navItem) =>
              navItem.filter((item) => item.label !== 'Chat')
            );
          }
        })
        .catch((err) => {
          console.log(err);
        });
    } else {
      setNavItems((navItem) => navItem.filter((item) => item.label !== 'Chat'));
    }
  }, [selectedGroup]);

  const container = React.useMemo(() => {
    // Blocked users are bounced out of the patient area (v5 matched this ahead
    // of every route in Switch order; v7 ranks by specificity, so gate first).
    if (user.block) {
      return <Navigate to={CONSTANTS.manageUserPath} replace />;
    }
    return (
      <Routes>
        <Route
          path={rel(CONSTANTS.patientDashboardPath)}
          element={<PatientDashboard />}
        />

        <Route
          path={rel(CONSTANTS.patientSettingsPath)}
          element={<PatientSettings />}
        />

        <Route
          path={rel(CONSTANTS.patientProband)}
          element={<PatientProband />}
        />

        <Route
          path={rel(CONSTANTS.patientPedigreeChartPath)}
          element={<PedigreeChartView patient={true} />}
        />

        {chatFeature && (
          <Route
            path={rel(CONSTANTS.patientChatPath)}
            element={<PatientChat />}
          />
        )}

        <Route
          path="*"
          element={<Navigate to={CONSTANTS.patientDashboardPath} replace />}
        />
      </Routes>
    );
  }, [user, user.block, chatFeature]);

  return (
    <>
      <LoadingContext.Provider value={loadingProviderValue}>
        <AppContext.Provider value={providerValue}>
          {container}
        </AppContext.Provider>
      </LoadingContext.Provider>
      <BackdropLoader loading={loading}> </BackdropLoader>
    </>
  );
}
